import { useEffect, useLayoutEffect, useRef, useState } from "preact/hooks";
import { prefersReducedMotion } from "./animation.js";
import { DON_MODE_MS } from "../../shared/donMode.js";
import type { DonModeRecord } from "../../shared/types.js";

/**
 * Don mode — the browser half of the app's one easter egg. The rules, and the
 * standings transforms they drive, are in `shared/donMode.ts`.
 *
 * What lives here is everything that only makes sense in front of a person: the
 * hidden gesture that sets it off, the flight of the stolen points across the board,
 * and the countdown that ends it. All of it is driven by one field on the tournament
 * snapshot, so a phone that was in a pocket when Don struck picks the reign up
 * wherever it has got to — and every phone in the group is watching the same minute.
 */

/** The heist itself: the beat where the points are in the air and the board is still
 * in its real order. Long enough to read as a robbery, short enough that nobody
 * wonders whether the screen has hung. Kept in step with the token flight in
 * `flyPoints` and with `.steal-token` in the stylesheet. */
export const DON_STEAL_MS = 900;

/**
 * How long the press has to be held.
 *
 * The gesture no longer has to be discoverable — the button says what to do — so this
 * is set by what the act deserves rather than by what someone might stumble into: long
 * enough for the fill to read as charging something up, short enough not to become a
 * chore. Kept in step with `.don-fire` in the stylesheet.
 */
export const DON_HOLD_MS = 800;

export interface DonMode {
  /** True while this tournament's one go is still there for the taking. */
  armed: boolean;
  /** Whose total the whole board's points are being counted into. Set for the theft
   * and for the reign that follows it. */
  thiefId: string | null;
  /** True only for the beat where the points are in the air: the board still shows its
   * real order, and the flight animation is running over the top of it. */
  stealing: boolean;
  /** Who is sitting on top of the board, or null. Set once the theft has landed. */
  crownedId: string | null;
  /** Whole seconds left in the reign, for the countdown. */
  secondsLeft: number;
  /** True from the moment a reign ends until the screen is left — the "that's it"
   * message needs to outlive the reign it's commenting on. */
  ended: boolean;
  /** Takes no player: this is only ever about the device's own, which is how the
   * Durable Object reads it too. */
  activate: () => void;
}

/**
 * Reads the tournament's Don-mode flag and turns it into what the screen needs.
 *
 * `record` comes from the synced snapshot rather than from this device, which is what
 * makes the board move for the whole group at once. `onActivate` sends the op — which
 * names nobody, because the only player it can be about is the one whose token sends
 * it. The Durable Object stamps the real timestamp and decides whether it was allowed,
 * and the snapshot that comes back replaces whatever the optimistic apply put there.
 *
 * The countdown runs off the server's timestamp against this device's clock. A phone
 * whose clock is wildly wrong will see a short reign or miss it altogether, which is
 * the right way round for a joke — the alternative, each device starting its own
 * minute whenever it happened to hear, means the group is never watching the same
 * thing, and that was the point of moving this off the device in the first place.
 */
export function useDonMode(record: DonModeRecord | null, onActivate: () => void): DonMode {
  const [now, setNow] = useState(() => Date.now());
  const [sawReign, setSawReign] = useState(false);

  const endsAt = record ? record.activatedAt + DON_MODE_MS : 0;
  const active = now < endsAt;
  const stealing = active && now < (record?.activatedAt ?? 0) + DON_STEAL_MS;

  // The clock, not a counter: a phone that locks mid-reign throttles timers to
  // nothing, and a counter would resume with a minute still to run. Ticking a
  // timestamp means it comes back to whatever is actually left, including none.
  //
  // Both effects below stop themselves: when the condition they guard on goes false
  // the effect re-runs and returns early, which is what clears the timer.
  useEffect(() => {
    if (!active) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [active, endsAt]);

  // The steal is shorter than the countdown's tick, so it needs its own alarm —
  // otherwise the climb to the top would land up to a second late, adrift from the
  // animation it's meant to follow.
  useEffect(() => {
    if (!stealing || !record) return;
    const timer = setTimeout(
      () => setNow(Date.now()),
      Math.max(0, record.activatedAt + DON_STEAL_MS - Date.now()),
    );
    return () => clearTimeout(timer);
  }, [stealing, record]);

  // Only counts as a reign this device actually saw. Arriving after it's over gets
  // the real board and no epitaph for a minute it missed.
  useEffect(() => {
    if (active) setSawReign(true);
  }, [active]);

  return {
    armed: record === null,
    thiefId: active ? (record?.playerId ?? null) : null,
    stealing,
    crownedId: active && !stealing ? (record?.playerId ?? null) : null,
    secondsLeft: active ? Math.ceil((endsAt - now) / 1000) : 0,
    ended: sawReign && !active,
    activate() {
      if (record) return;
      onActivate();
      // The optimistic apply in `client.ts` has already put a record on the snapshot,
      // so this render is the one that starts the heist on the thief's own phone —
      // everyone else's starts when the broadcast reaches them, a moment later.
      setNow(Date.now());
    },
  };
}

/** One token's flight, and the most that each may wait behind the one before it. The
 * stagger is squeezed below that as the group grows (see `flyPoints`) so a society
 * day's worth of tokens still lands inside the same beat a four-ball's does. */
const FLIGHT_MS = 620;
const FLIGHT_STAGGER_MS = 60;

/**
 * The theft, as seen: each rival's total lifts off its row, arcs across the board and
 * drops into Don's.
 *
 * This is drawn rather than laid out — the tokens are fixed-position clones parked on
 * `document.body`, animated with transform and opacity only, and thrown away when they
 * land. Nothing about the real rows changes while they fly, so the list can reorder
 * the moment they arrive without the two fighting over the same elements.
 *
 * `stolen` carries the amounts because the rendered numbers have already been zeroed
 * by the time this runs — the count-down on each row and the flight are the same event
 * seen twice, and reading the DOM for the old value would be reading a number that is
 * on its way out.
 *
 * Returns a teardown that cancels anything still in the air, so leaving the screen
 * mid-heist doesn't leave tokens stranded over whatever comes next.
 */
export function flyPoints(
  board: HTMLElement,
  thiefId: string,
  stolen: Map<string, number>,
): () => void {
  const target = board.querySelector(`[data-player="${thiefId}"] .lb-points`);
  // Reduced motion gets the outcome without the flight: the totals still change, they
  // just don't take a lap of the screen to do it.
  if (!target || prefersReducedMotion()) return () => {};

  const to = target.getBoundingClientRect();
  const tokens: HTMLElement[] = [];
  const running: Animation[] = [];
  // Everything has to be in the air and landed within the steal, however many players
  // are in the game — a sixteen-strong society day would otherwise still be throwing
  // tokens a second after Don had reached the top of the board.
  const flying = [...stolen].filter(([id, amount]) => id !== thiefId && amount !== 0);
  const stagger = Math.min(
    FLIGHT_STAGGER_MS,
    (DON_STEAL_MS - FLIGHT_MS) / Math.max(1, flying.length - 1),
  );
  let index = 0;

  for (const [playerId, amount] of flying) {
    const source = board.querySelector(`[data-player="${playerId}"] .lb-points`);
    if (!source) continue;

    const from = source.getBoundingClientRect();
    const token = document.createElement("span");
    token.className = "steal-token";
    token.textContent = String(amount);
    // Off the accessibility tree: a screen reader is already being told the totals
    // changed by the rows themselves, and narrating the confetti would be noise.
    token.setAttribute("aria-hidden", "true");
    token.style.left = `${from.left}px`;
    token.style.top = `${from.top}px`;
    document.body.appendChild(token);
    tokens.push(token);

    const dx = to.left - from.left;
    const dy = to.top - from.top;
    const delay = index++ * stagger;

    running.push(
      token.animate(
        [
          { transform: "translate(0, 0) scale(1)", opacity: 0 },
          { transform: "translate(0, -6px) scale(1.15)", opacity: 1, offset: 0.15 },
          // Lifted off the straight line between the two rows, so a token crossing a
          // row it isn't from reads as passing over the board rather than through it.
          {
            transform: `translate(${dx * 0.5}px, ${dy * 0.5 - 34}px) scale(1.15)`,
            opacity: 1,
            offset: 0.6,
          },
          { transform: `translate(${dx}px, ${dy}px) scale(0.6)`, opacity: 0 },
        ],
        { duration: FLIGHT_MS, delay, easing: "cubic-bezier(0.4, 0, 0.2, 1)", fill: "backwards" },
      ),
    );

    // The catch: the thief's total flinches as each one lands.
    running.push(
      target.animate([{ scale: 1 }, { scale: 1.18 }, { scale: 1 }], {
        duration: 260,
        delay: delay + FLIGHT_MS - 90,
        easing: "ease-out",
      }),
    );
  }

  const clear = () => {
    for (const token of tokens) token.remove();
  };
  Promise.all(running.map((a) => a.finished)).then(clear, clear);

  return () => {
    for (const animation of running) animation.cancel();
    clear();
  };
}

/**
 * Runs the flight once, when the heist starts on this device.
 *
 * A layout effect rather than an effect: it measures where the rows are, and by the
 * time a passive effect ran the browser could already have painted a frame with the
 * zeroed totals in it — the tokens would then leave a row that had visibly emptied a
 * moment earlier.
 *
 * Two things it deliberately doesn't do. It doesn't fire for a device that arrives
 * after the steal is over — a phone unlocked halfway through the reign joins a board
 * Don has already robbed, so there is nothing left to see fly. And it doesn't tear the
 * flight down when the steal beat ends, only when the screen goes: the two are within
 * a few frames of each other, and the wrong order there would cancel the last token in
 * mid-air.
 */
export function useHeist(
  board: { current: HTMLElement | null },
  stealing: boolean,
  thiefId: string | null,
  stolen: Map<string, number>,
) {
  // Held in a ref so the amounts don't have to be a stable dependency — the effect
  // fires on the heist starting, and reads whatever the board was worth at that moment.
  const amounts = useRef(stolen);
  amounts.current = stolen;
  const teardown = useRef<() => void>(() => {});
  const flown = useRef(false);

  useLayoutEffect(() => {
    if (!stealing || !thiefId || flown.current || !board.current) return;
    flown.current = true;
    teardown.current = flyPoints(board.current, thiefId, amounts.current);
  }, [stealing, thiefId]);

  useLayoutEffect(() => () => teardown.current(), []);
}

/** How far a pointer may wander before the hold is treated as a scroll. */
const HOLD_SLOP_PX = 12;

export interface HoldProps {
  onPointerDown: (e: PointerEvent) => void;
  onPointerMove: (e: PointerEvent) => void;
  onPointerUp: () => void;
  onPointerLeave: () => void;
  onPointerCancel: () => void;
  onContextMenu: (e: Event) => void;
  onClick: (e: Event) => void;
}

/**
 * Press-and-hold, with a fallback for a press that didn't last.
 *
 * A hold rather than a tap because this is the one irreversible thing in the app that
 * nobody can undo: one per tournament, gone the moment it fires. A button you have to
 * lean on can't be triggered by a thumb landing badly on a scroll.
 *
 * `onShortPress` is what a press that let go too early gets — on a labelled
 * hold-to-fire button that's the prompt to try again, not a second feature. Keyboard
 * activation is the exception and fires the hold outright: `click` with `detail === 0`
 * is Enter or Space, where there is no press to lengthen.
 *
 * `onHold` being null leaves a plain button — the handlers are still attached, they
 * just have nothing to do, which keeps the hook call unconditional.
 */
export function useHold(onHold: (() => void) | null, onShortPress: () => void): HoldProps {
  const timer = useRef(0);
  const origin = useRef<{ x: number; y: number } | null>(null);
  const held = useRef(false);

  const cancel = () => {
    clearTimeout(timer.current);
    origin.current = null;
  };

  useEffect(() => () => clearTimeout(timer.current), []);

  return {
    onPointerDown(e: PointerEvent) {
      // Secondary buttons open menus; a right-click isn't a hold.
      if (e.button !== 0 || !onHold) return;
      held.current = false;
      origin.current = { x: e.clientX, y: e.clientY };
      timer.current = window.setTimeout(() => {
        held.current = true;
        onHold();
      }, DON_HOLD_MS);
    },
    onPointerMove(e: PointerEvent) {
      const from = origin.current;
      if (!from) return;
      if (Math.hypot(e.clientX - from.x, e.clientY - from.y) > HOLD_SLOP_PX) cancel();
    },
    onPointerUp: cancel,
    onPointerLeave: cancel,
    // Fired when the browser takes the pointer over for a scroll, which is the common
    // way a hold on a list is really a drag.
    onPointerCancel: cancel,
    onContextMenu(e: Event) {
      // Long-press on touch raises the context menu over the top of the reveal.
      if (onHold) e.preventDefault();
    },
    onClick(e: Event) {
      // The click that follows a completed hold is swallowed: it would otherwise be
      // read as a second, separate press.
      if (held.current) {
        held.current = false;
        e.preventDefault();
        return;
      }
      // Enter or Space. There is no press to lengthen, so asking for a longer one
      // would put the feature out of reach entirely.
      if (onHold && (e as MouseEvent).detail === 0) {
        onHold();
        return;
      }
      onShortPress();
    },
  };
}

/**
 * The one-shot handoff between the button that fires Don mode and the board it happens
 * on — see `PlayerTotal` and `Leaderboard`.
 *
 * The theft's first beat is every rival's total draining away, and there is nothing to
 * watch drain on a board that mounts already robbed. So the button doesn't send the
 * op: it arms this, navigates to the standings, and lets the board fire once it is
 * mounted and showing the real numbers.
 *
 * Module state rather than storage, deliberately — an intent that survived a reload
 * would fire a minute of robbery at somebody who had just opened the app.
 */
let pendingActivation = false;

export function armDonMode() {
  pendingActivation = true;
}

export function takePendingDonMode(): boolean {
  const pending = pendingActivation;
  pendingActivation = false;
  return pending;
}
